<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKanbanStore } from 'dashboard/stores/kanban';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import KanbanBoard from 'dashboard/components-next/Kanban/KanbanBoard.vue';

const { t } = useI18n();
const kanbanStore = useKanbanStore();

const boards = computed(() => kanbanStore.list);
const activeBoardId = ref(null);

const activeBoard = computed(() =>
  boards.value.find(b => b.id === activeBoardId.value)
);

const isCreating = ref(false);
const newBoardName = ref('');

const selectBoard = id => {
  activeBoardId.value = id;
};

const submitNewBoard = async () => {
  if (!newBoardName.value.trim()) return;
  const board = await kanbanStore.createBoard(newBoardName.value);
  newBoardName.value = '';
  isCreating.value = false;
  activeBoardId.value = board.id;
};

const deleteActiveBoard = () => {
  if (!activeBoard.value) return;
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('KANBAN.INDEX.DELETE_BOARD_CONFIRM', { name: activeBoard.value.name })
    )
  ) {
    return;
  }
  kanbanStore.deleteBoard(activeBoard.value.id);
  activeBoardId.value = boards.value[0]?.id || null;
};

onMounted(async () => {
  await kanbanStore.fetch();
  activeBoardId.value = boards.value[0]?.id || null;
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <!-- Header: board tabs + actions -->
    <div
      class="flex items-center justify-between gap-4 px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <div class="flex items-center gap-2 overflow-x-auto">
        <h1 class="text-lg font-semibold text-n-slate-12 m-0 mr-2">
          {{ t('KANBAN.INDEX.TITLE') }}
        </h1>
        <Button
          v-for="board in boards"
          :key="board.id"
          :label="board.name"
          :variant="board.id === activeBoardId ? 'solid' : 'outline'"
          :color="board.id === activeBoardId ? 'blue' : 'slate'"
          size="sm"
          @click="selectBoard(board.id)"
        />
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <Button
          v-if="activeBoard"
          variant="ghost"
          color="ruby"
          size="sm"
          icon="i-lucide-trash-2"
          :label="t('KANBAN.INDEX.DELETE_BOARD')"
          @click="deleteActiveBoard"
        />
        <div v-if="isCreating" class="flex items-center gap-2">
          <Input
            v-model="newBoardName"
            :placeholder="t('KANBAN.INDEX.NEW_BOARD_PLACEHOLDER')"
            autofocus
            @keyup.enter="submitNewBoard"
          />
          <Button
            :label="t('KANBAN.INDEX.CREATE')"
            color="blue"
            size="sm"
            @click="submitNewBoard"
          />
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            @click="isCreating = false"
          />
        </div>
        <Button
          v-else
          :label="t('KANBAN.INDEX.NEW_BOARD')"
          color="blue"
          size="sm"
          icon="i-lucide-plus"
          @click="isCreating = true"
        />
      </div>
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-hidden p-6">
      <div
        v-if="boards.length === 0"
        class="flex flex-col items-center justify-center gap-3 h-full rounded-xl border border-dashed border-n-weak"
      >
        <span class="i-lucide-trello size-10 text-n-slate-9" />
        <span class="text-base text-n-slate-11">
          {{ t('KANBAN.INDEX.EMPTY_TITLE') }}
        </span>
        <Button
          :label="t('KANBAN.INDEX.NEW_BOARD')"
          variant="outline"
          icon="i-lucide-plus"
          @click="isCreating = true"
        />
      </div>

      <KanbanBoard v-else-if="activeBoard" :key="activeBoard.id" :board="activeBoard" />

      <div
        v-else
        class="flex items-center justify-center h-full text-n-slate-11 text-base"
      >
        {{ t('KANBAN.INDEX.SELECT_BOARD') }}
      </div>
    </div>
  </div>
</template>
